'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect } from 'react';

import { CompetitionStatusEnum } from '@flipster/apis/v1/tradingCompetition/types';
import { FormattedMessage } from '@flipster/i18n';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { css } from '@flipster/styled-system/css';
import { alert } from '@flipster/ui/Alert';
import { checkAxiosError } from '@flipster/utils/axios';
import { getErrorCode } from '@flipster/utils/error';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { LoadingPage } from '../loading-page';
import { SegmentPage } from '../misc/segment/segment-page';
import { CompetitionIdContext, competitionBunja, competitionRankingBunja } from './bunja';
import { CollectingResults } from './collecting-results';
import { Hero } from './hero';
import { LastWeekChampionView } from './last-week-champion-view';
import { MyInfo } from './my-info';
import { Overlay } from './overlay';
import { RegisterNowButton } from './register-now-button';
import { RegistrationMetaView } from './registration-meta-view';
import { Rules } from './rules';
import { BannerSection } from './section/banner';
import { EventInfoSection } from './section/event-info';
import { PrizeSection } from './section/prize';
import { Ranking } from './section/ranking';

export const CompetitionsPage = () => {
  const { id } = useParams<{ id: string }>();

  return (
    <CompetitionIdContext value={id}>
      <Suspense>
        <Page />
      </Suspense>
    </CompetitionIdContext>
  );
};

const Page = () => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const { id, competitionAtom, competitionErrorAtom, registerCompetition, isNewDesignTargetAtom } =
    useBunja(competitionBunja);
  const { isLessThanAwardListAtom } = useBunja(competitionRankingBunja);
  const competition = useAtomValue(competitionAtom);
  const competitionError = useAtomValue(competitionErrorAtom);
  const isLessThanAwardList = useAtomValue(isLessThanAwardListAtom);
  const isNewDesignTarget = useAtomValue(isNewDesignTargetAtom);

  useEffect(() => {
    if (!competition || competition?.participationTime) return;
    const invitationCode = searchParams.get('invitationCode');
    if (invitationCode) {
      (async () => {
        try {
          await registerCompetition(invitationCode);
          snackbar.success({
            message: <FormattedMessage id='Autogen_common.r251126.s_Your_registration_has_been_successful' />,
          });
        } catch {
          snackbar.error({
            message: <FormattedMessage id='Autogen_common.r251126.s_Your_registration_could_not_be_completed' />,
          });
        }
      })();
    }
  }, [competition, searchParams, registerCompetition]);

  useEffect(() => {
    if (checkAxiosError(competitionError)) {
      const errorCode = getErrorCode(competitionError);
      if (errorCode === 'api.tradingCompetition.competitionNotFound') router.push('/404');
      else alert.error(competitionError);
    }
  }, [router, competitionError]);

  if (competition?.competitionStatus === CompetitionStatusEnum.Canceled) router.push('/404');
  if (!competition) return <LoadingPage />;

  const isLeaderboardVisible =
    (competition.competitionType === 'WEEKLY_PNL' || !!competition.participationTime) &&
    competition.isLeaderboardVisible;
  const styleValues: Record<string, string | undefined> = {
    '--banner-bg-color': `#${competition.backgroundColorBanner}`,
    '--info-bg-left-color': `#${competition.backgroundColorLeft}`,
    '--info-bg-right-color': `#${competition.backgroundColorRight}`,
  };

  return (
    <>
      <SegmentPage name={CompetitionsEvent.CompetitionLandingView} context={{ competition_id: id }} />
      <main className={css({ w: '100%', pb: 30, bg: 'gray.black' })} style={styleValues}>
        <div className={css({ pos: 'relative' })}>
          {isNewDesignTarget ? <NewCompetitionView /> : <OldCompetitionView />}
        </div>
        {/* 등록한 유저 && 리더보더 보이기 옵션 && 4명 이하 && 이벤트 진행중 */}
        {isLeaderboardVisible &&
          isLessThanAwardList &&
          competition.competitionStatus === CompetitionStatusEnum.Ongoing && <CollectingResults />}
        {/* 등록한 유저 && 리더보더 보이기 옵션 && 4명 이상 && 이벤트 시작전 아닌 경우 */}
        {isLeaderboardVisible &&
          !isLessThanAwardList &&
          competition.competitionStatus !== CompetitionStatusEnum.Upcoming && <Ranking />}
        <Rules markdown={competition.noticeMarkdown} />
      </main>
    </>
  );
};

const NewCompetitionView = () => {
  const { competitionAtom, registrationStatusAtom, registrationMetaAtom } = useBunja(competitionBunja);
  const competition = useAtomValue(competitionAtom);
  const registrationStatus = useAtomValue(registrationStatusAtom);
  const registrationMeta = useAtomValue(registrationMetaAtom);

  if (!competition) return null;

  return (
    <>
      <Hero />
      <PrizeSection />
      {registrationMeta && (
        <RegistrationMetaView title={registrationMeta.title} description={registrationMeta.description} />
      )}
      {(competition.competitionType == 'WEEKLY_PNL' || competition.participationTime) && <MyInfo />}
      {competition.recurringLastWeekChampion && (
        <LastWeekChampionView champion={competition.recurringLastWeekChampion} />
      )}
      {/* 등록 기간 && 등록하지 않음 */}
      {competition.competitionType != 'WEEKLY_PNL' &&
        registrationStatus?.isBetween &&
        !competition.participationTime && <RegisterNowButton />}
    </>
  );
};

const OldCompetitionView = () => {
  const { competitionAtom, overlayTextAtom } = useBunja(competitionBunja);
  const competition = useAtomValue(competitionAtom);
  const overlayText = useAtomValue(overlayTextAtom);
  if (!competition) return null;
  return (
    <>
      <BannerSection competition={competition} />
      <Overlay text={overlayText} />
      <EventInfoSection />
    </>
  );
};
